import { useStore } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled, { css } from 'styled-components';

import { formatPanelNumber } from '../../utility/panelFieldValues';

import type { ElementState } from '../../renderer/ElementState';

interface PropertyNumericalInputProps {
  activeElement?: ElementState;
  propertyName: string;
  defaultValue: any;
  unit?: string; // some properties don't have units, e.g. aspect ratio
  getInputValue?: any;
  manualKaraoke?: boolean;
  padding?: string;
  customStyles?: string;
  disabled?: boolean;
  /** Says what unlocks a disabled field, on hover (design system: a disabled
   *  control explains itself). */
  disabledReason?: string;
}

function byString(o: any, s: string) {
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, ''); // strip a leading dot
  var a = s.split('.');
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
}

export const PropertyNumericalInput: React.FC<PropertyNumericalInputProps> =
  observer(props => {
    const { editorStore } = useStore();
    const elementByString = () => {
      if (!props.activeElement) return null;
      return byString(props.activeElement?.source, props.propertyName);
    };
    const [value, setValue] = React.useState(
      () => elementByString() ?? props.defaultValue,
    );
    const [changed, setChanged] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [badInput, setBadInput] = React.useState<string>('');
    const { unit } = props;

    React.useEffect(() => {
      let value = elementByString() ?? props.defaultValue;
      setValue(value);
      if (badInput) {
        doHandleSetBadInput(String(value));
      }
    }, [
      props.activeElement?.source,
      elementByString(),
      props.propertyName,
      props.defaultValue,
    ]);

    let displayValue = value;
    if (!isNaN(value) && !editing) {
      displayValue = `${formatPanelNumber(value)}${unit ? ` ${unit}` : ''}`;
    }

    const badInputStyle = `
      border: 1px solid var(--destructive);
    `;
    const CUSTOM_STYLE = `${props.customStyles || ''} ${
      badInput ? badInputStyle : ''
    }`;

    const excludeUnit = (value: string): string => {
      if (!unit) return value;

      // Trim the value to handle any whitespace before checking
      const trimmedValue = value.trim();

      // Check if the trimmed value ends with the unit
      if (trimmedValue.endsWith(unit) && trimmedValue !== unit) {
        // Find the index where the unit starts
        const unitIndex = trimmedValue.lastIndexOf(unit);
        // Get everything before the unit and trim any trailing whitespace
        return trimmedValue.substring(0, unitIndex).trimEnd();
      }
      return value;
    };

    const formatValue = (value: string): string => {
      if (unit) {
        return `${value.trim()}${unit}`;
      }
      return value;
    };

    const handleSetBadInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      return doHandleSetBadInput(e.target.value);
    };

    const doHandleSetBadInput = (value: string): boolean => {
      value = excludeUnit(value);
      if (isNaN(Number(value))) {
        setBadInput('Please enter a number');
        return true;
      } else if (Number(value) < 0) {
        setBadInput('Please enter a positive number');
        return true;
      }
      setBadInput('');
      return false;
    };

    return (
      <Container title={props.disabled ? props.disabledReason : undefined}>
        <TextInput
          value={displayValue}
          padding={props.padding}
          manualKaraoke={props.manualKaraoke}
          customStyles={CUSTOM_STYLE}
          disabled={props.disabled}
          onFocus={e => {
            if (props.disabled) return;
            setEditing(true);
            // Edit the same rounded number the field showed, not the raw one.
            if (!isNaN(value)) setValue(formatPanelNumber(value));
            e.target.select();
            setChanged(false);
          }}
          onChange={async e => {
            if (props.disabled) return;
            handleSetBadInput(e);
            setValue(e.target.value);
            setChanged(true);
          }}
          onBlur={async e => {
            if (props.disabled) return;
            const hasBadInput = handleSetBadInput(e);
            if (hasBadInput) return;
            if (changed && props.getInputValue) {
              setEditing(false);
              const value = excludeUnit(e.target.value);
              return await props.getInputValue(
                props.propertyName,
                value,
                formatValue(value),
              );
            }
            if (
              changed &&
              props.activeElement &&
              e.target.value !== elementByString()
            ) {
              // Use the headless editor UpdateElementCommand
              const elementId = props.activeElement.source.id;
              const value = excludeUnit(e.target.value);

              // Convert string values to appropriate types
              let typedValue: any = value;
              if (!isNaN(Number(value))) {
                typedValue = Number(value);
              }

              // Use the command-based update system
              editorStore.updateElementProperty(
                elementId,
                props.propertyName,
                typedValue,
              );
            }
            setEditing(false);
          }}
        />
        {badInput && <ErrorText>{badInput}</ErrorText>}
      </Container>
    );
  });

const Container = styled.div`
  width: 100%;
  min-width: 0;
`;

const TextInput = styled.input<{
  manualKaraoke?: boolean;
  padding?: string;
  customStyles?: string;
  disabled?: boolean;
}>`
  display: flex;
  resize: none;
  padding: 10px 15px;
  margin: 10px 0;
  color: var(--foreground);
  background: var(--popover);
  border: none;
  border-radius: var(--radius);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  height: 34px;

  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}

  ${props =>
    props.manualKaraoke &&
    css`
      outline: none;
      border: 1px solid var(--border);
      background: transparent;
      color: var(--text-secondary);
      margin: 0;
      padding: ${props.padding ?? '14px 15px'};
      border-radius: var(--radius);
      width: inherit;

      &:focus {
        outline: 0;
      }
    `}

  ${props =>
    props.customStyles &&
    css`
      ${props.customStyles}
    `}
`;

const ErrorText = styled.div`
  display: flex;
  min-width: 80px;
  color: var(--destructive);
  font-family: var(--font-primary);
  font-size: var(--text-xs);

  font-weight: var(--font-weight-normal);
`;
